import React, { useState, FormEvent, useEffect } from 'react';
import { inviteTeamMember } from '@api/team-member';
import { roles } from '@typings/ProfileTypes';
import { ApiResponse } from '@typings/ApiResponse';
import { CarehomeStaff } from '@typings/TeamMemberTypes';
import ArrowDownIcon from '@assets/icons/arrow-down-icon.svg';
import InfoIcon from '@assets/icons/info-icon.svg';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import getErrorMessage from '@utils/errorUtils';
import NewErrorSummary from '@components/NewErrorSummary';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
  isValidEmail,
} from '@utils/commons';
import SuccessNotificationBanner from '../SuccessNotificationBanner';
import './styles.scss';

interface AddTeamMemberProps {
  partnerCarehomeId: string;
  onFormSubmit: () => void;
}

const validateEmail = (email: string) => {
  const value = email.trim();
  if (!value) {
    return "Please enter person's email address";
  }

  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }

  return '';
};

function AddTeamMember({
  partnerCarehomeId,
  onFormSubmit,
}: AddTeamMemberProps) {
  const [showInviteInstruction, setShowInviteInstruction] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [addedRole, setAddedRole] = useState('');
  const [addTeamMemberStatus, setAddTeamMemberStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const [errors, setErrors] = useState<FormError[]>([]);
  const [errMessage, setErrMessage] = useState<{
    fullName: string | undefined;
    email: string | undefined;
    addRole: string | undefined;
  }>({
    fullName: '',
    email: '',
    addRole: '',
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];
    validationErrors = updateErrors(
      validationErrors,
      'fullName',
      !fullName ? "Enter the person's full name" : null
    );
    validationErrors = updateErrors(
      validationErrors,
      'email',
      validateEmail(email)
    );
    validationErrors = updateErrors(
      validationErrors,
      'addRole',
      !addedRole ? 'Please select a role' : ''
    );

    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    // If no errors, proceed with form submission
    setAddTeamMemberStatus(prev => ({ ...prev, loading: true }));
    try {
      const response: ApiResponse<CarehomeStaff | string> =
        await inviteTeamMember(fullName, email, addedRole, partnerCarehomeId);
      if (response) {
        if (response.status === 'success') {
          setAddTeamMemberStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message: `${fullName} has been invited`,
          }));
          setFullName('');
          setEmail('');
          setAddedRole('');
          onFormSubmit();

          setTimeout(() => {
            setAddTeamMemberStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      setAddTeamMemberStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });

      validationErrors = updateErrors(
        validationErrors,
        'addTeamMemberAPI',
        errorMessage
      );
      setErrors(prev => validationErrors);
    } finally {
      setAddTeamMemberStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setErrMessage({
      fullName: getErrorMessageFromErrors(errors, 'fullName'),
      email: getErrorMessageFromErrors(errors, 'email'),
      addRole: getErrorMessageFromErrors(errors, 'addRole'),
    });
  }, [errors]);

  return (
    <div className="add-team-member mb-5 mt-5">
      <h3 className="govuk-heading-m">Invite Your Colleagues</h3>

      <div className="govuk-accordion__section">
        <div
          className="govuk-accordion__section-header"
          onClick={() => setShowInviteInstruction(!showInviteInstruction)}
        >
          <h2 className="govuk-accordion__section-heading">
            <img width={25} src={InfoIcon} alt={InfoIcon} className="mr-1" />
            <span
              className="govuk-accordion__section-button"
              id="accordion-default-heading-1"
            >
              Learn about inviting colleagues
            </span>
            <img
              width={30}
              src={ArrowDownIcon}
              alt={ArrowDownIcon}
              className={`arrow-icon ${showInviteInstruction ? '' : 'rotate-180'}`}
            />
          </h2>
        </div>
        {showInviteInstruction && (
          <div
            id="accordion-default-content-1"
            className="govuk-accordion__section-content mb-5"
          >
            <p className="govuk-body">
              You can invite other &apos;Managers&apos; and &apos;Team
              Members&apos;
              <span className="underline">at your organisation</span> who
              support your community with their Advance Care Plans. Once
              invited, they will receive an email and they need to setup their
              own account using the invitation link provided in the email.
              Managers at your organisation are in charge and responsible for
              colleagues who are invited. Managers at your organisation are also
              responsible for removing colleagues when they leave your
              organisation and no longer support your community with their
              Advance Care Plans.
            </p>
            <p className="govuk-body">
              When you assign other &apos;Managers&apos; just like you, they
              will be able to invite other &apos;Team Members&apos; and
              &apos;Managers&apos; for your organisation. The majority of those
              in your organisation should be invited as a &apos;Team
              Member&apos;. Team Members have full access to your &apos;MyWishes
              Care&apos; environment and can carry out Advance Care Planning
              tasks. &apos;Team Members&apos; however cannot access this page
              and invite other colleagues to join your organisation in MyWishes
              Care.
            </p>
          </div>
        )}
      </div>

      <form>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        {addTeamMemberStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            content={addTeamMemberStatus.message}
            title=""
            duration={3000}
            noScrollToTop
          />
        )}
        <div
          className={`govuk-form-group ${errMessage.fullName ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="fullName">
            Full name
          </label>
          {errMessage.fullName && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errMessage.fullName}
            </p>
          )}
          <input
            className={`govuk-input ${errMessage.fullName ? 'govuk-input--error' : ''}`}
            id="fullName"
            name="fullName"
            type="text"
            placeholder="Please enter a name"
            aria-describedby={
              errMessage.fullName ? 'full-name-input-error' : undefined
            }
            autoComplete="fullName"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
          />
        </div>
        <div
          className={`govuk-form-group ${errMessage.email ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="email">
            Add your colleagues email address
          </label>
          {errMessage.email && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {errMessage.email}
            </p>
          )}
          <input
            className={`govuk-input ${errMessage.email ? 'govuk-input--error' : ''}`}
            id="email"
            name="email"
            type="email"
            spellCheck="false"
            aria-describedby="email-hint"
            autoComplete="email"
            placeholder="Enter email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div
          className={`govuk-form-group ${errMessage.addRole ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="addRole">
            Choose administrative role
          </label>
          {errMessage.addRole && (
            <p id="role-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span> Please
              select a role
            </p>
          )}

          <select
            className={`govuk-select govuk-!-width-full ${errMessage.addRole ? 'govuk-select--error' : ''}`}
            id="addRole"
            name="addRole"
            aria-describedby="role-hint role-error"
            value={addedRole}
            onChange={e => setAddedRole(e.target.value)}
          >
            <option value="" disabled>
              Please select a role
            </option>
            {roles.map(role => {
              return (
                <option key={role.id} value={role.value}>
                  {role.label}
                </option>
              );
            })}
          </select>
        </div>
        <div className="govuk-button-group">
          <button
            type="submit"
            className="govuk-button"
            disabled={addTeamMemberStatus.loading}
            aria-disabled={addTeamMemberStatus.loading}
            onClick={handleSubmit}
          >
            Invite now
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddTeamMember;
